import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../../layouts/MainLayout';
import EmployeeForm from '../../../components/hr/EmployeeForm';
import { Card, Button, Space, message } from 'antd';
import { PlusOutlined, ArrowRightOutlined, UserOutlined } from '@ant-design/icons';
import { employeesApi } from '../../../utils/api';
import { formatJalaliApiDate } from '../../../utils/dateUtils';

const AddEmployee = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const resolveId = (...candidates) => {
    for (const raw of candidates) {
      if (raw == null || raw === '') continue;
      if (typeof raw === 'object') {
        const nested = raw.id ?? raw.value;
        if (nested != null && nested !== '') return nested;
        continue;
      }
      return raw;
    }
    return null;
  };

  const appendValue = (formData, key, value) => {
    if (value == null || value === '') return;
    formData.append(key, String(value));
  };

  const appendNested = (formData, prefix, value) => {
    if (value == null) return;
    if (Array.isArray(value)) {
      value.forEach((item, index) => appendNested(formData, `${prefix}[${index}]`, item));
      return;
    }
    if (typeof value === 'object' && !(value instanceof Date) && !(value instanceof File)) {
      Object.entries(value).forEach(([k, v]) => appendNested(formData, `${prefix}[${k}]`, v));
      return;
    }
    formData.append(prefix, String(value));
  };

  const buildPayload = (values) => {
    const departmentId = resolveId(
      values.department_id,
      values.employee_detail?.department?.id,
      values.employee_detail?.department,
      values.department?.id,
      values.department,
    );
    const designationId = resolveId(
      values.designation_id,
      values.employee_detail?.designation?.id,
      values.employee_detail?.designation,
      values.designation?.id,
      values.designation,
    );

    const payload = {
      employee_id: values.employee_id || null,
      name: values.name || '',
      email: values.email || '',
      mobile: values.mobile || '',
      password: values.password || '',
      gender: values.gender || null,
      national_id: values.national_id || null,
      father_name: values.father_name || null,
      insurance_code: values.insurance_code || null,
      locale: values.locale || 'fa',
      login: values.login || 'yes',
      email_notifications: values.email_notifications || 'yes',
      tags: values.tags || null,
      employee_detail: {
        employee_id: values.employee_detail?.employee_id || values.employee_id || null,
        department: departmentId != null ? { id: departmentId } : null,
        designation: designationId != null ? { id: designationId } : null,
        date_of_birth: formatJalaliApiDate(values.date_of_birth || values.employee_detail?.date_of_birth),
        joining_date: formatJalaliApiDate(values.joining_date || values.employee_detail?.joining_date),
        address: values.address || values.employee_detail?.address || null,
        reporting_to: values.reporting_to || values.employee_detail?.reporting_to || null,
        about_me: values.about_me || values.employee_detail?.about_me || null,
        hourly_rate: values.hourly_rate ? Number(values.hourly_rate) : (values.employee_detail?.hourly_rate ? Number(values.employee_detail.hourly_rate) : null),
        probation_end_date: formatJalaliApiDate(values.probation_end_date || values.employee_detail?.probation_end_date),
        notice_period_start_date: formatJalaliApiDate(values.notice_period_start_date || values.employee_detail?.notice_period_start_date),
        notice_period_end_date: formatJalaliApiDate(values.notice_period_end_date || values.employee_detail?.notice_period_end_date),
        employment_type: values.employment_type || values.employee_detail?.employment_type || null,
        internship_end_date: formatJalaliApiDate(values.internship_end_date || values.employee_detail?.internship_end_date),
        contract_end_date: formatJalaliApiDate(values.contract_end_date || values.employee_detail?.contract_end_date),
        marital_status: values.marital_status || values.employee_detail?.marital_status || null,
        company_address_id: values.company_address_id || values.company_address || values.employee_detail?.company_address_id || null,
      },
    };

    return payload;
  };

  const buildEmployeeFormData = (payload, fileOptions = {}) => {
    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (key === 'employee_detail' && value && typeof value === 'object') {
        appendNested(formData, 'employee_detail', value);
        return;
      }
      appendValue(formData, key, value);
    });
    if (fileOptions.imageFile) {
      formData.append('image', fileOptions.imageFile);
    }
    return formData;
  };

  const handleSave = async (values, fileOptions) => {
    setLoading(true);
    try {
      const payload = buildPayload(values);
      const body = buildEmployeeFormData(payload, fileOptions);
      await employeesApi.create(body);
      message.success('کارمند با موفقیت ثبت شد');
      navigate('/hr/employees');
    } catch (error) {
      console.error('خطا در ذخیره:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-green-50 dark:bg-green-900/20 flex items-center justify-center">
                <UserOutlined className="text-green-600 dark:text-green-400 text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">افزودن کارمند جدید</h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">اطلاعات کارمند را تکمیل و ثبت کنید</p>
              </div>
            </div>
            <Space>
              <Button icon={<ArrowRightOutlined />} onClick={() => navigate('/hr/employees')} style={{ fontFamily: 'CustomFont' }}>
                بازگشت
              </Button>
              <Button type="primary" icon={<PlusOutlined />} disabled>
                راهنما
              </Button>
            </Space>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            <EmployeeForm
              onSave={handleSave}
              onCancel={() => navigate('/hr/employees')}
              loading={loading}
              isEdit={false}
            />
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default AddEmployee;
